import React, { Fragment, useState, FC } from 'react';
import { OrderingProvider, useOrdering } from '../../stores/OrderingStore';
import OrderingPage from './OrderingPage';
import IKioskOrderingRepository from '../../domain/repositories/IKioskOrderingRepository';
import IOrderingRepository from '../../domain/repositories/IOrderingRepository';
import {
  BaseOrderingStore,
  OnlineOrder,
  ResultResponse,
  KioskOrderingStore,
  PromoCode
} from 'kiolyn-api';
import { isNull, isNullOrUndefined } from 'util';
import ShowSnackbars from './ShowSnackbars/ShowSnackbars';
import { isEmpty } from 'lodash';
import SubmitKioskOrder from './SubmitKioskOrder/SubmitKioskOrder';
import { useObserver } from 'mobx-react-lite';
import { ViewState } from '../../domain/models/ViewState';
import { LayoutVariant } from '../Core/CommonTypes';
import { Box, Button, Grid, makeStyles, Typography } from '@material-ui/core';
import visa from 'payment-icons/min/flat/visa.svg';
import master from 'payment-icons/min/flat/mastercard.svg';
import amex from 'payment-icons/min/flat/amex.svg';
import discover from 'payment-icons/min/flat/discover.svg';
import { useNavigation } from 'react-navi';
import IdleTime, { TypeIdleTime } from '../App/IdleTime';
import { getTypeOrderKiosk } from '../../domain/usecases/GetTypeOrderKiosk';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(1),
    display: 'flex'
  },
  wrapper: {
    flex: 1
  },
  title: {
    marginRight: 48
  },
  bizHour: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    display: 'table-cell',
    verticalAlign: 'bottom'
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  menuTitle: {
    lineHeight: 1.2,
    margin: theme.spacing(2)
  },
  paddindClass: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5)
  },
  imgsvg: {
    height: '5%',
    width: '5%',
    margin: theme.spacing(1) / 2
  },
  imgmobilesvg: {
    height: '12%',
    width: '12%',
    margin: theme.spacing(1) / 2
  },
  buttonMargin: {
    margin: theme.spacing(1) / 2
  },
  icon: {
    marginRight: theme.spacing(1)
  }
}));

function isInvalidKiosk(kiosk: KioskOrderingStore): boolean {
  return (
    isNullOrUndefined(kiosk) ||
    isNullOrUndefined(kiosk.main) ||
    isEmpty(kiosk.main) ||
    isNullOrUndefined(kiosk.ccdevice) ||
    isEmpty(kiosk.ccdevice)
  );
}

class OrderingRepository implements IOrderingRepository {
  kioskOrderingRepository: IKioskOrderingRepository;
  storeid: string;
  kioskid: string;
  main?: string;
  type?: string;

  constructor(
    kioskOrderingRepository: IKioskOrderingRepository,
    storeid: string,
    kioskid: string,
    main?: string,
    type?: string
  ) {
    this.kioskOrderingRepository = kioskOrderingRepository;
    this.storeid = storeid;
    this.kioskid = kioskid;
    this.main = main;
    this.type = type;
  }

  public async loadStore(): Promise<BaseOrderingStore> {
    const store = await this.kioskOrderingRepository.loadKiosk(
      this.storeid,
      this.kioskid
    );
    if (!isNullOrUndefined(this.main)) {
      store.main = this.main;
    }
    store.accept_order = !isInvalidKiosk(store);
    return store;
  }

  public submitOrder(
    order: OnlineOrder,
    store: BaseOrderingStore,
    token: string,
    ignorePayment: boolean
  ): Promise<ResultResponse> {
    const kiosk = store as KioskOrderingStore;
    const main = this.main || kiosk.main;
    this.type = getTypeOrderKiosk(true) as string;

    if (
      isNullOrUndefined(main) ||
      isEmpty(main) ||
      isNullOrUndefined(kiosk.id) ||
      isEmpty(kiosk.id)
    ) {
      return Promise.reject('Missing Main IP address.');
    }
    if (isNullOrUndefined(kiosk.ccdevice)) {
      return Promise.reject(
        'Credit card device is not configured for this kiosk.'
      );
    }

    return this.kioskOrderingRepository.submitOrder(
      kiosk.id,
      main,
      this.type,
      order,
      kiosk.ccdevice,
      ignorePayment,
      kiosk.printer
    );
  }

  public getPromoCodeByName(name: string): Promise<PromoCode> {
    throw new Error('Method not implemented.');
  }

  public getWebsocketForSubmitOrder(): EventSource {
    throw new Error('Method not implemented.');
  }

  public checkOrderStatus(orderId: string): Promise<boolean> {
    throw new Error('Method not implemented.');
  }
}

interface KioskOrderingPageProps {
  storeid: string;
  kioskid: string;
  main?: string;
  type?: string;
  kioskOrderingRepository: IKioskOrderingRepository;
}

export default ({
  storeid,
  kioskid,
  main,
  type,
  kioskOrderingRepository
}: KioskOrderingPageProps) => {
  const orderingRepository = new OrderingRepository(
    kioskOrderingRepository,
    storeid,
    kioskid,
    main,
    type
  );
  return (
    <OrderingProvider orderingRepository={orderingRepository}>
      <KioskOrderingPage
        storeid={storeid}
        kioskid={kioskid}
        main={main}
        ktype={type}
      />
    </OrderingProvider>
  );
};

interface KioskOrderingPage2Props {
  storeid: string;
  kioskid: string;
  main?: string;
  ktype?: string;
}

const KioskOrderingPage: FC<KioskOrderingPage2Props> = ({
  storeid,
  kioskid,
  main,
  ktype
}: KioskOrderingPage2Props) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const navigation = useNavigation();

  const [showSubmitOrderDialog, setShowSubmitOrderDialog] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showInfoButton, setShowInfoButton] = useState(true);
  const [showInfoOrderDialog, setShowInfoOrderDialog] = useState(false);

  const cards: { [name: string]: string } = { visa, master, amex, discover };
  const landingPageURL = `/store/${storeid}/kiosk/${kioskid}?main=${main}`;

  const handleOnclick = (kiosktype: string) => {
    navigation.navigate(landingPageURL + `&type=${kiosktype}`);
  };

  const handleSubmitOrderClose = () => {
    setShowSubmitOrderDialog(false);
    // Reset the submitting state
    orderingStore.submitState = ViewState.NONE;
    navigation.navigate(`/store/${storeid}/kiosk/${kioskid}?main=${main}`);
  };

  return useObserver(() => {
    const kioskStore = orderingStore.store as KioskOrderingStore;
    const dineInTextNotEmpty =
      !isNullOrUndefined(
        kioskStore.kiosk_landing_page_settings?.dine_in_order_button_text
      ) &&
      kioskStore.kiosk_landing_page_settings?.dine_in_order_button_text.trim() !==
        '';
    const toGoTextNotEmpty =
      !isNullOrUndefined(
        kioskStore.kiosk_landing_page_settings?.to_go_order_button_text
      ) &&
      kioskStore.kiosk_landing_page_settings?.to_go_order_button_text.trim() !==
        '';

    return (
      <Fragment>
        {kioskStore.kiosk_landing_page_settings?.enable_landing_page &&
          (dineInTextNotEmpty || toGoTextNotEmpty) &&
          isNullOrUndefined(ktype) && (
            <Box
              width="100%"
              height="35%"
              justifyContent="center"
              display="table"
            >
              <Box
                width="100%"
                justifyContent="center"
                className={classes.bizHour}
              >
                <Typography variant="h3" className={classes.menuTitle}>
                  {isNull(kioskStore) ? 'Store not found' : kioskStore.name}
                </Typography>
                {kioskStore.kiosk_landing_page_settings?.text_line &&
                  !isNullOrUndefined(
                    kioskStore.kiosk_landing_page_settings.text_1
                  ) && (
                    <Typography variant="h6" className={classes.menuTitle}>
                      {kioskStore.kiosk_landing_page_settings.text_1}
                    </Typography>
                  )}
                {kioskStore.kiosk_landing_page_settings?.text_line &&
                  !isNullOrUndefined(
                    kioskStore.kiosk_landing_page_settings.text_2
                  ) && (
                    <Typography variant="h6" className={classes.menuTitle}>
                      {kioskStore.kiosk_landing_page_settings.text_2}
                    </Typography>
                  )}
                <Grid
                  container
                  direction="row"
                  justify="center"
                  className={classes.paddindClass}
                >
                  {dineInTextNotEmpty && (
                    <Button
                      className={classes.buttonMargin}
                      onClick={() => {
                        handleOnclick('dinein');
                      }}
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      {
                        kioskStore.kiosk_landing_page_settings
                          ?.dine_in_order_button_text
                      }
                    </Button>
                  )}
                  {toGoTextNotEmpty && (
                    <Button
                      className={classes.buttonMargin}
                      onClick={() => {
                        handleOnclick('togo');
                      }}
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      {
                        kioskStore.kiosk_landing_page_settings
                          ?.to_go_order_button_text
                      }
                    </Button>
                  )}
                </Grid>

                <Grid
                  container
                  direction="row"
                  justify="center"
                  className={classes.paddindClass}
                >
                  {Object.keys(cards).map(key => (
                    <img
                      src={cards[key]}
                      alt={key}
                      key={key}
                      className={classes.imgsvg}
                    />
                  ))}
                </Grid>
              </Box>
            </Box>
          )}

        {(!kioskStore.kiosk_landing_page_settings?.enable_landing_page ||
          !isNullOrUndefined(ktype)) && (
          <OrderingPage
            onSubmit={() => setShowSubmitOrderDialog(true)}
            variant={LayoutVariant.kiosk}
            homeURL={landingPageURL}
            showOrderDetail={showOrderDetail}
            setShowOrderDetail={setShowOrderDetail}
            showInfoButton={showInfoButton}
            showInfoOrder={showInfoOrderDialog}
            setShowInfoButton={setShowInfoButton}
            setShowInfoOrderDialog={setShowInfoOrderDialog}
            defaultView={kioskStore.kiosk_landing_page_settings?.kiosk_view}
          />
        )}

        <InvalidSettingsSnackbar />
        {showSubmitOrderDialog && (
          <SubmitKioskOrder onClose={handleSubmitOrderClose} />
        )}

        <IdleTime type={TypeIdleTime.Kiosk} />
      </Fragment>
    );
  });
};

const InvalidSettingsSnackbar: FC<{}> = () => {
  const orderingStore = useOrdering();

  return useObserver(() => {
    const kioskStore = orderingStore.store as KioskOrderingStore;
    const invalidSettings = isInvalidKiosk(kioskStore);

    if (!invalidSettings || orderingStore.viewState !== ViewState.OK) {
      return null;
    }

    let message = '';
    if (isEmpty(kioskStore.main)) {
      message = 'Main station is not configured for this Kiosk.';
    } else if (isEmpty(kioskStore.ccdevice)) {
      message = 'Credit card device is not configured for this Kiosk.';
    }

    return (
      <ShowSnackbars
        message={message}
        variant="error"
        vertical="bottom"
        horizontal="left"
      />
    );
  });
};
